import React from "react";
import LandingNavbar from "../landing/LandingNavbar";
import Footer from "../landing/Footer";
import { typography, surfaces, textColors, brandColors } from "../../design-system/tokens";
import { APP_DOMAIN } from '../../core/utils/config';

const PrivacyPolicy = () => {
  return (
    <div className={`min-h-screen ${surfaces.secondary} flex flex-col`}>
      <LandingNavbar />
      
      <div className="flex-1 pt-16">
        {/* Brand Gradient Header Section */}
        <div className={`bg-gradient-to-r ${brandColors.gradientFrom} ${brandColors.gradientTo} text-white`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <h1 className={typography.h1Dark}>
                Privacy Policy
              </h1>
              <p className={`${typography.bodyLargeDark} mt-4`}>
                Last updated April 6, 2026
              </p>
            </div>
          </div>
        </div>

        {/* Privacy Policy Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className={`${surfaces.primary} rounded-lg shadow-md p-8 space-y-8`}>
            
            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Introduction</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                Welcome to {APP_DOMAIN} ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website building platform and related services (the "Service").
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Personal Information</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    We collect information that you provide directly to us, including:
                  </p>
                  <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} mt-2 space-y-1`}>
                    <li>Name and email address when you create an account</li>
                    <li>Profile information and website content you create</li>
                    <li>Communication preferences and support requests</li>
                    <li>Payment information for premium services</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Automatically Collected Information</h3>
                  <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                    We automatically collect certain information about your device and usage patterns:
                  </p>
                  <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} mt-2 space-y-1`}>
                    <li>IP address, browser type, and operating system</li>
                    <li>Pages visited and time spent on our platform</li>
                    <li>Device identifiers and usage analytics</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>How We Use Your Information</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-4`}>
                We use the information we collect for the following purposes:
              </p>
              <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                <li>To provide, maintain, and improve our website building services</li>
                <li>To create and manage your account and website projects</li>
                <li>To process payments and send billing communications</li>
                <li>To provide customer support and respond to your inquiries</li>
                <li>To send important service updates and promotional communications</li>
                <li>To analyze usage patterns and improve our platform functionality</li>
                <li>To prevent fraud and ensure platform security</li>
              </ul>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Information Sharing and Disclosure</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-4`}>
                We do not sell your personal information. We may share your information in the following circumstances:
              </p>
              <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                <li>With service providers who assist in operating our platform</li>
                <li>When required by law or to protect our legal rights</li>
                <li>In connection with a business transfer or acquisition</li>
                <li>With your explicit consent for specific purposes</li>
                <li>Public information on websites you choose to make public</li>
              </ul>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Third-Party Service Providers</h2>
              <div className="space-y-3">
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  We work with third-party service providers who perform services on our behalf. These providers are given access to your information only as necessary to perform their functions and are obligated to protect it. Categories of providers we may use include:
                </p>
                <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                  <li>Cloud hosting and infrastructure (e.g., Amazon Web Services)</li>
                  <li>Payment processing services</li>
                  <li>Email delivery and communication services</li>
                  <li>Analytics and performance monitoring</li>
                  <li>Authentication providers (e.g., Google OAuth)</li>
                  <li>Content delivery networks</li>
                </ul>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  We require our service providers to process personal data only in accordance with our instructions and applicable law.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Data Security</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                We use administrative, technical, and physical safeguards designed to protect your personal information. However, no method of transmission over the internet or method of electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>International Data Transfers</h2>
              <div className="space-y-3">
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  Our Service is operated from the United States. If you are accessing the Service from outside the United States, please be aware that your information will be transferred to, stored, and processed in the United States, where our servers are located and our databases are operated. The data protection laws of the United States may differ from those of your jurisdiction.
                </p>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  By using the Service, you acknowledge that your information will be transferred to and processed in the United States in accordance with this Privacy Policy. Where required by applicable law, we will implement appropriate safeguards (such as standard contractual clauses or other approved mechanisms) for the transfer of personal data.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Your Rights and Choices</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed mb-4`}>
                Depending on your location and subject to applicable law, you may have certain rights regarding your personal information, including the ability to:
              </p>
              <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                <li>Access and review the personal information we have about you</li>
                <li>Request correction of inaccurate or incomplete information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Download your data in a portable format</li>
                <li>Restrict processing of your information in certain circumstances</li>
              </ul>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed mt-4`}>
                To make a request, contact us using the details in the Contact Information section below. We may need to verify your identity before fulfilling certain requests.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>California Privacy Rights</h2>
              <div className="space-y-3">
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  If you are a California resident, the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA) provide you with additional rights regarding your personal information. These include:
                </p>
                <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                  <li><span className="font-medium">Right to Know:</span> You have the right to request that we disclose the categories and specific pieces of personal information we have collected about you, the categories of sources, the business purpose for collecting it, and the categories of third parties with whom we share it.</li>
                  <li><span className="font-medium">Right to Delete:</span> You have the right to request that we delete your personal information, subject to certain exceptions.</li>
                  <li><span className="font-medium">Right to Correct:</span> You have the right to request correction of inaccurate personal information.</li>
                  <li><span className="font-medium">Right to Opt-Out of Sale or Sharing:</span> We do not sell your personal information. We do not share your personal information for cross-context behavioral advertising purposes.</li>
                  <li><span className="font-medium">Right to Non-Discrimination:</span> We will not discriminate against you for exercising any of your privacy rights.</li>
                </ul>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  To exercise your California privacy rights, please contact us at{" "}
                  <a className="underline" href={`mailto:privacy@${APP_DOMAIN}`}>privacy@{APP_DOMAIN}</a>.
                  {" "}We will verify your identity before processing your request. You may also designate an authorized agent to make a request on your behalf.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Cookies and Tracking</h2>
              <div className={`space-y-4 ${typography.body} ${textColors.secondary} leading-relaxed`}>
                <p>
                  We use cookies and similar technologies to operate and secure our services. Some cookies are strictly necessary and are used only to provide the service you request.
                </p>
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Strictly Necessary Cookies</h3>
                  <ul className="list-disc list-inside space-y-1">
                    <li>
                      <span className="font-medium">Session cookie</span> (e.g., <span className="font-mono">sessionid</span>): keeps you signed in and allows us to process your requests securely. Without it, core features like creating and editing your website will not work.
                    </li>
                    <li>
                      <span className="font-medium">CSRF cookie</span> (e.g., <span className="font-mono">csrftoken</span>): may be set to help protect against cross‑site request forgery in certain forms or pages.
                    </li>
                  </ul>
                  <p className="mt-2">
                    These cookies are essential to the functioning of the platform and are set when you sign in or interact with secure areas. They may be session‑based or may persist for a limited time, depending on your browser settings and the Service configuration.
                  </p>
                </div>
                <div>
                  <h3 className={`${typography.h4} ${textColors.primary} mb-2`}>Preferences and Analytics</h3>
                  <p>
                    We may use additional cookies to remember your preferences and to understand how the platform is used so we can improve it. You can control cookie preferences through your browser settings. If you disable cookies, some features may not function properly.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Do Not Track Signals</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                Some web browsers transmit "Do Not Track" (DNT) signals. Because there is no universally accepted standard for how to respond to DNT signals, the Service does not currently respond to or alter its practices when it receives DNT signals from your browser. We will continue to monitor developments around DNT browser technology and update our practices as appropriate.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Children's Privacy</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                Our Service is intended for users who are at least 18 years old or who have reached the age of majority in their jurisdiction. We do not knowingly collect personal information from anyone under 18. If we become aware that we have collected personal information from a person under 18, we will take steps to delete that information promptly. If you believe we may have collected information from a minor, please contact us at{" "}
                <a className="underline" href={`mailto:privacy@${APP_DOMAIN}`}>privacy@{APP_DOMAIN}</a>.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Visitors to User-Created Websites</h2>
              <div className="space-y-3">
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  Our platform allows users to create and publish websites that may be visited by third parties ("Website Visitors"). When you visit a website created by one of our users, the following applies:
                </p>
                <ul className={`list-disc list-inside ${typography.body} ${textColors.secondary} space-y-2`}>
                  <li>The user who created the website is the data controller for any personal information collected through their website. The user is responsible for their own privacy practices and legal compliance.</li>
                  <li>{APP_DOMAIN} acts as a data processor (or service provider) on behalf of the user, processing data only as necessary to host and deliver the website.</li>
                  <li>We may collect limited technical information (such as IP addresses and access logs) for security, performance, and service operation purposes.</li>
                  <li>For questions about how your data is used on a specific user-created website, please contact the website owner directly.</li>
                </ul>
                <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                  This Privacy Policy applies to {APP_DOMAIN}'s own data collection and practices. It does not apply to data practices of our users or the websites they create.
                </p>
              </div>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Data Retention</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                We retain personal information for as long as necessary to provide the Service and for legitimate business purposes, such as complying with legal obligations, resolving disputes, and enforcing our agreements. Retention periods may vary depending on the type of information and how it is used.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Data Breach Notification</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                In the event of a data breach that affects your personal information, we will notify affected users and applicable regulatory authorities as required by law. Notification will be made in a timely manner and will include, to the extent known, the nature of the breach, the categories of data affected, and the measures we are taking to address and mitigate the breach.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Changes to This Policy</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of significant changes by posting the updated policy on our website and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className={`${typography.h2} ${textColors.primary} mb-4`}>Contact Information</h2>
              <p className={`${typography.body} ${textColors.secondary} leading-relaxed`}>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className={`mt-4 p-4 ${surfaces.secondary} rounded-lg`}>
                <p className={`${typography.body} ${textColors.primary} font-medium`}>{APP_DOMAIN} Privacy Team</p>
                <p className={`${typography.body} ${textColors.secondary}`}>
                  Email:{" "}
                  <a className="underline" href={`mailto:privacy@${APP_DOMAIN}`}>
                    privacy@{APP_DOMAIN}
                  </a>
                </p>
                <p className={`${typography.body} ${textColors.secondary}`}>
                  We will respond within the timeframe required by applicable law.
                </p>
              </div>
            </section>

          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;